import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import toast from "react-hot-toast";
import PageContentLayout from "../layout/PageContentLayout";

/**
 * Reusable school list table for billed / not-billed / expired views.
 */
const SchoolStatusTable = ({
  title,
  subtitle,
  breadcrumbLabel,
  breadcrumbIcon,
  backTo = "/schools",
  banner,
  fetchRows,
  columns,
  getRowKey = (row) => row.school_code,
  searchFields = (row) => [row.school_name, row.admin_name, row.state, row.country],
  itemLabel = "school",
  searchPlaceholder = "Search schools...",
  emptyLabel,
  showSerial = false,
  refreshKey = 0,
  polished = false,
  getRowSx,
  onRowClick,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const result = await fetchRows();
    if (result.success) {
      setRows(Array.isArray(result.data) ? result.data : []);
    } else {
      toast.error(result.error || `Failed to load ${itemLabel}s`);
      setRows([]);
    }
    setLoading(false);
  }, [fetchRows, itemLabel]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const filtered = rows.filter((row) => {
    const haystack = searchFields(row).filter(Boolean).join(" ").toLowerCase();
    return haystack.includes(searchTerm.toLowerCase());
  });

  const colSpan = columns.length + (showSerial ? 1 : 0);

  return (
    <PageContentLayout
      title={title}
      subtitle={subtitle}
      barSubtitle={`${rows.length} ${itemLabel}${rows.length === 1 ? "" : "s"}`}
      breadcrumbs={[{ label: breadcrumbLabel, icon: breadcrumbIcon }]}
      backTo={backTo}
      darkBar
    >
      {banner}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>
      <Card
        sx={
          polished
            ? {
                overflow: "hidden",
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "0 10px 28px rgba(15, 23, 42, 0.06)",
              }
            : undefined
        }
      >
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small" sx={polished ? { minWidth: 920 } : undefined}>
                <TableHead>
                  <TableRow
                    sx={
                      polished
                        ? {
                            bgcolor: "rgba(15, 23, 42, 0.04)",
                            "& th": {
                              fontWeight: 800,
                              fontSize: 12,
                              letterSpacing: "0.04em",
                              textTransform: "uppercase",
                              color: "text.secondary",
                              borderBottomColor: "divider",
                              py: 1.35,
                              whiteSpace: "nowrap",
                            },
                          }
                        : undefined
                    }
                  >
                    {showSerial ? (
                      <TableCell sx={{ width: 56, fontWeight: 700 }}>#</TableCell>
                    ) : null}
                    {columns.map((col) => (
                      <TableCell key={col.id} align={col.align || "left"}>
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {emptyLabel || `No ${itemLabel}s found`}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map((row, index) => (
                      <TableRow
                        key={getRowKey(row)}
                        hover
                        onClick={onRowClick ? () => onRowClick(row) : undefined}
                        sx={{
                          ...(onRowClick ? { cursor: "pointer" } : {}),
                          ...(polished
                            ? {
                                transition: "background 0.15s ease",
                                "& td": {
                                  py: 1.35,
                                  borderBottomColor: "rgba(148, 163, 184, 0.22)",
                                },
                                "&:last-of-type td": { borderBottom: 0 },
                              }
                            : {}),
                          ...(typeof getRowSx === "function" ? getRowSx(row, index) : {}),
                        }}
                      >
                        {showSerial ? (
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 800,
                                color: polished ? "text.secondary" : "inherit",
                              }}
                            >
                              {index + 1}
                            </Typography>
                          </TableCell>
                        ) : null}
                        {columns.map((col) => (
                          <TableCell key={col.id} align={col.align || "left"}>
                            {col.render(row, index)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </PageContentLayout>
  );
};

export default SchoolStatusTable;
